import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { GET_BOOKING_BY_QR } from 'graphql/booking/queries'
import { t } from 'i18next'
import { QrCodeIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import { playSuccessBeep, playErrorBeep } from 'utils/beepSound'

/**
 * QRScannerPage Component
 * 
 * Integrates with Zebra CR2278-PC USB scanner for QR check-in
 * - Captures QR data via USB HID keyboard mode
 * - Parses booking QR code
 * - Shows visual & audio confirmation
 * - Redirects to check-in page with booking details
 */
export default function QRScannerPage() {
    const bufferRef = useRef('')
    const timeoutRef = useRef(null)
    const [scanProgress, setScanProgress] = useState(0)
    const [totalChunks, setTotalChunks] = useState(0)
    const chunksRef = useRef(new Map())
    const [scanError, setScanError] = useState(null)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const navigate = useNavigate()

    // Reset chunk state - memoized function
    const resetChunkState = useCallback(() => {
        chunksRef.current.clear()
        setScanProgress(0)
        setTotalChunks(0)
        bufferRef.current = ''
    }, [])

    // Handle errors - memoized function
    const handleScanError = useCallback((message) => {
        playErrorBeep().catch(console.error)
        setScanError(message)
        setErrorMessage(message)
        setShowErrDialog(true)
        resetChunkState()
    }, [resetChunkState])

    // Query to get booking by QR code
    const [getBookingByQR, { loading }] = useLazyQuery(GET_BOOKING_BY_QR, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data?.getBookingByQR?.success && data?.getBookingByQR?.booking) {
                const booking = data.getBookingByQR.booking

                // Play success beep sound
                playSuccessBeep().catch(console.error)
                setScanError(null)

                // Navigate to booking details with the first segment's travel date
                const firstSegment = booking.trip_segments?.[0]
                const travelDate = firstSegment?.travel_date || booking.metadata?.travel_date
                const bookingType = booking.metadata?.booking_type?.code
                const bookingId = booking.booking_id

                // Redirect to booking details immediately
                const path = bookingType
                    ? `/manage-boat-booking/details/${bookingId}/${travelDate}/${bookingType}`
                    : `/manage-boat-booking/details/${bookingId}/${travelDate}`

                navigate(path, {
                    state: {
                        isCheckInMode: true,
                        fromQRScan: true
                    }
                })
            } else {
                const message = data?.getBookingByQR?.message || t('Booking not found')
                handleScanError(message)
            }
        },
        onError: (error) => {
            const message = error.message || t('Failed to retrieve booking information')
            handleScanError(message)
        }
    })

    // Process complete QR code
    const processCompleteQR = useCallback((qrCode) => {
        getBookingByQR({
            variables: { qrCode }
        })
    }, [getBookingByQR])

    // Process chunk data
    const processChunk = useCallback((rawData) => {
        // Quick validation
        if (!rawData || rawData.length < 3) return

        const pipeIndex1 = rawData.indexOf('|')
        if (pipeIndex1 === -1) {
            // Single chunk (no pipe delimiter) - treat as complete QR
            processCompleteQR(rawData)
            return
        }

        const pipeIndex2 = rawData.indexOf('|', pipeIndex1 + 1)
        if (pipeIndex2 === -1) {
            handleScanError(t('Invalid QR code format'))
            return
        }

        // Parse chunk metadata efficiently
        const chunkIndex = rawData.substring(0, pipeIndex1)
        const totalChunksStr = rawData.substring(pipeIndex1 + 1, pipeIndex2)
        const chunkData = rawData.substring(pipeIndex2 + 1)

        const index = parseInt(chunkIndex, 10)
        const total = parseInt(totalChunksStr, 10)

        // Validate parsed values
        if (isNaN(index) || isNaN(total) || index < 1 || total < 1 || index > total) {
            handleScanError(t('Invalid QR code format'))
            return
        }

        // First chunk - initialize
        if (index === 1) {
            chunksRef.current.clear()
            setTotalChunks(total)
            setScanProgress(0)
        }

        // Store chunk
        chunksRef.current.set(index, chunkData)

        const receivedChunks = chunksRef.current.size
        const progress = Math.round((receivedChunks / total) * 100)
        setScanProgress(progress)

        // Check if all chunks received
        if (receivedChunks === total) {
            // Combine chunks efficiently using array
            const chunks = new Array(total)
            for (let i = 1; i <= total; i++) {
                const chunk = chunksRef.current.get(i)
                if (!chunk) {
                    handleScanError(t('Missing QR code chunks'))
                    return
                }
                chunks[i - 1] = chunk
            }

            const completeQR = chunks.join('')
            resetChunkState()
            processCompleteQR(completeQR)
        }
    }, [processCompleteQR, handleScanError, resetChunkState])

    // Handle keyboard input from Zebra scanner
    useEffect(() => {

        const handleKeyPress = (e) => {
            // Ignore typing in input/textarea
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return

            // Add key to buffer
            bufferRef.current += e.key

            // Clear previous timeout
            if (timeoutRef.current) clearTimeout(timeoutRef.current)

            // Set timeout to detect end of scan
            timeoutRef.current = setTimeout(() => {
                const scannedData = bufferRef.current.trim()
                if (scannedData.length > 0) {

                    // Process the scanned data through chunk handler
                    processChunk(scannedData)
                }
                bufferRef.current = "" // reset buffer
            }, 300) // 150ms is usually enough for Zebra scanners
        }

        window.addEventListener("keypress", handleKeyPress)

        return () => {
            window.removeEventListener("keypress", handleKeyPress)
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
        }
    }, [processChunk])

    return (
        <>
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-2xl w-full">
                    {/* Main Scanner Card */}
                    <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                        {/* Scanner Icon */}
                        <div className="flex justify-center mb-6">
                            <div className="relative">
                                <QrCodeIcon className="h-32 w-32 text-primary" />
                                {loading && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Title */}
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">
                            {t('QR Code Check-In')}
                        </h1>

                        {/* Instructions */}
                        <div className="space-y-3 mb-8">
                            <p className="text-lg text-gray-600">
                                {t('Point the Zebra scanner at the QR code to check in')}
                            </p>
                            <p className="text-sm text-gray-500">
                                {t('The system will automatically detect and process the QR code')}
                            </p>
                        </div>

                        {/* Status Messages */}
                        {(loading || totalChunks > 0) && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                <p className="text-blue-800 font-medium">
                                    {loading ? t('Processing QR code...') :
                                        t('Scanning large QR code: {{progress}}%', { progress: scanProgress })}
                                </p>
                                {totalChunks > 0 && scanProgress < 100 && (
                                    <div className="mt-2 w-full bg-blue-200 rounded-full h-2.5">
                                        <div
                                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                                            style={{ width: `${scanProgress}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {scanError && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center justify-center gap-2">
                                <ExclamationCircleIcon className="h-5 w-5 text-red-600" />
                                <p className="text-red-800 font-medium">
                                    {scanError}
                                </p>
                            </div>
                        )}

                        {/* Scanner Info */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span>{t('Scanner Ready')}</span>
                            </div>
                            <p className="text-xs text-gray-400 mt-2">
                                {t('Zebra CR2278-PC Scanner')}
                            </p>
                        </div>

                        {/* Manual Entry Option */}
                        <div className="mt-6">
                            <button
                                onClick={() => navigate('/check-in')}
                                className="text-sm text-primary hover:text-primary/80 font-medium"
                            >
                                {t('Or search booking manually')} →
                            </button>
                        </div>
                    </div>

                    {/* Help Card */}
                    <div className="mt-6 bg-blue-50 rounded-lg p-6">
                        <h3 className="text-sm font-semibold text-blue-900 mb-3">
                            {t('Scanner Tips')}
                        </h3>
                        <ul className="space-y-2 text-sm text-blue-800">
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 mt-0.5">•</span>
                                <span>{t('Hold the QR code steady and ensure good lighting')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 mt-0.5">•</span>
                                <span>{t('Keep the QR code within 4-6 inches of the scanner')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-600 mt-0.5">•</span>
                                <span>{t('Wait for the beep sound to confirm successful scan')}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Error Dialog */}
            <ErrorDialog
                status={showErrDialog}
                title={t('Error')}
                onClose={() => {
                    setShowErrDialog(false)
                    setScanError(null)
                }}
                message={errorMessage}
            />
        </>
    )
}
